import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Card,
  Table,
  Spinner,
  Button,
  Badge,
  Alert,
  Modal,
  Form,
} from "react-bootstrap";
import { useAuth } from "../../contexts/AuthContext";
import { fires } from "../../firebase";
import { useNavigate } from "react-router-dom";

const STATUS_OPTIONS = ["pending", "paid", "cancelled"];

export default function AdminAllPayouts() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payouts, setPayouts] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [payoutToEdit, setPayoutToEdit] = useState(null);
  const [newStatus, setNewStatus] = useState("");

  const monthlySummary = useMemo(() => {
    const summaryMap = new Map();

    payouts.forEach((p) => {
      const baseDate = p.eventEndDate || p.createdAt;
      if (!baseDate) return;
      const d = baseDate instanceof Date ? baseDate : new Date(baseDate);
      if (isNaN(d.getTime())) return;

      const year = d.getFullYear();
      const month = d.getMonth(); // 0-based
      const key = `${year}-${month}`;

      if (!summaryMap.has(key)) {
        summaryMap.set(key, {
          year,
          month,
          netPayout: 0,
          grossAmount: 0,
          egFee: 0,
          stripeFee: 0,
        });
      }

      const row = summaryMap.get(key);
      const currencySafe = p.currency; // reserved for future multi-currency handling

      row.netPayout += Number(p.amount || 0);
      row.grossAmount += Number(p.totalAmount || 0);
      row.egFee += Number(p.totalEgFee || 0);
      row.stripeFee += Number(p.totalStripeFee || 0);
    });

    return Array.from(summaryMap.values()).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });
  }, [payouts]);

  useEffect(() => {
    checkAdmin();
  }, [currentUser]);

  useEffect(() => {
    if (isAdmin) {
      loadPayouts();
    }
  }, [isAdmin]);

  const checkAdmin = async () => {
    try {
      if (!currentUser) {
        navigate("/");
        return;
      }
      const adminDoc = await fires.collection("admin").doc(currentUser.uid).get();
      if (!adminDoc.exists) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      const roles = adminDoc.data()?.roles || [];
      if (!roles.includes("management")) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      setIsAdmin(true);
    } catch (err) {
      console.error("Admin check failed:", err);
      setError("Failed to verify access");
      setLoading(false);
    }
  };

  const loadPayouts = async () => {
    setLoading(true);
    setError("");
    try {
      let snapshot;
      try {
        snapshot = await fires
          .collection("payouts")
          .orderBy("createdAt", "desc")
          .get();
      } catch (e) {
        if (e.code === "failed-precondition" || e.code === "unavailable") {
          snapshot = await fires.collection("payouts").get();
        } else throw e;
      }

      const baseList = snapshot.docs.map((doc) => {
        const d = doc.data();
        const createdAt = d.createdAt?.toDate?.() ?? d.timestamp?.toDate?.() ?? (d.createdAt ? new Date(d.createdAt) : null) ?? (d.timestamp ? new Date(d.timestamp) : null);
        const netPayout = d.totals?.netPayout ?? d.amount ?? 0;
        const amount = typeof netPayout === "number" ? netPayout : parseFloat(netPayout) || 0;
        return {
          id: doc.id,
          eventId: d.eventId ?? "",
          eventTitle: d.eventTitle ?? d.eventName ?? "",
          orgId: d.orgId ?? "",
          currency: d.currency ?? "CAD",
          status: d.status ?? "pending",
          amount,
          totalAmount: d.totals?.totalAmount ?? null,
          totalEgFee: d.totals?.totalEgFee ?? null,
          totalStripeFee: d.totals?.totalStripeFee ?? null,
          numberOfOrders: d.numberOfOrders ?? (d.orders?.length ?? 0),
          createdAt,
          createdBy: d.createdBy ?? null,
          description: d.description ?? null,
          orders: d.orders ?? [],
          eventEndDate: null,
        };
      });

      // Fetch event end dates for all payouts with an eventId
      const eventIdSet = new Set(baseList.map(p => p.eventId).filter(id => id));
      const eventEndMap = new Map();
      for (const id of eventIdSet) {
        try {
          const evDoc = await fires.collection("events").doc(id).get();
          if (evDoc.exists) {
            const ev = evDoc.data();
            const end = ev.endDate?.toDate?.() ?? ev.endDate ?? null;
            const endDate =
              end instanceof Date
                ? end
                : end
                ? new Date(end)
                : null;
            if (endDate && !isNaN(endDate.getTime())) {
              eventEndMap.set(id, endDate);
            }
          }
        } catch (e) {
          console.warn("Failed to fetch event end date for", id, e);
        }
      }

      const list = baseList.map(p => ({
        ...p,
        eventEndDate: eventEndMap.get(p.eventId) || null,
      }));

      if (!snapshot.query?.orderBy) {
        list.sort((a, b) => (b.createdAt ? b.createdAt.getTime() : 0) - (a.createdAt ? a.createdAt.getTime() : 0));
      }
      setPayouts(list);
    } catch (err) {
      console.error("Error loading payouts:", err);
      setError(err?.message || "Failed to load payouts");
      setPayouts([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value, currency = "CAD") => {
    if (value == null || isNaN(Number(value))) return "—";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency || "CAD",
    }).format(Number(value));
  };

  const formatDate = (date) => {
    if (!date) return "—";
    const d = date instanceof Date ? date : new Date(date);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
  };

  const statusVariant = (status) => {
    if (!status) return "secondary";
    const s = String(status).toLowerCase();
    if (s === "paid") return "success";
    if (s === "pending") return "warning";
    if (s === "cancelled") return "danger";
    return "secondary";
  };

  const openStatusModal = (payout) => {
    setPayoutToEdit(payout);
    setNewStatus(payout?.status || "pending");
    setShowStatusModal(true);
  };

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setPayoutToEdit(null);
    setNewStatus("");
  };

  const confirmUpdateStatus = async () => {
    if (!payoutToEdit || !newStatus) return;
    setUpdatingId(payoutToEdit.id);
    setError("");
    try {
      await fires.collection("payouts").doc(payoutToEdit.id).update({ status: newStatus });
      setPayouts((prev) =>
        prev.map((p) => (p.id === payoutToEdit.id ? { ...p, status: newStatus } : p))
      );
      closeStatusModal();
    } catch (err) {
      console.error("Error updating payout status:", err);
      setError(err?.message || "Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  if (!currentUser || loading) {
    return (
      <Container className="py-5">
        <div className="d-flex justify-content-center align-items-center">
          <Spinner animation="border" />
          <span className="ms-2">Loading...</span>
        </div>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container className="py-5">
        <Alert variant="danger">Access denied. Admin only.</Alert>
        <Button variant="outline-primary" onClick={() => navigate("/")}>
          Back
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">All Payouts</h2>
          <p className="text-muted small mb-0">Admin-only list from collection &quot;payouts&quot;</p>
        </div>
        <Button variant="outline-primary" onClick={loadPayouts} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 mb-0 text-muted">Loading payouts...</p>
            </div>
          ) : payouts.length === 0 ? (
            <div className="text-center py-5 text-muted">No payouts found.</div>
          ) : (
            <Table responsive hover size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Event</th>
                  <th>Event ID</th>
                  <th>Org ID</th>
                  <th className="text-end">Net Payout</th>
                  <th className="text-end">Gross Amount</th>
                  <th className="text-end">EG Fee</th>
                  <th className="text-end">Stripe Fee</th>
                  <th className="text-end">Orders</th>
                  <th>Event End</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Created by</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p, idx) => (
                  <tr key={p.id}>
                    <td className="text-muted">{idx + 1}</td>
                    <td>{p.eventTitle || "—"}</td>
                    <td>
                      <code className="small">{p.eventId || "—"}</code>
                    </td>
                    <td>{p.orgId ? <code className="small">{p.orgId}</code> : "—"}</td>
                    <td className="text-end">{formatCurrency(p.amount, p.currency)}</td>
                    <td className="text-end">
                      {p.totalAmount != null ? formatCurrency(p.totalAmount, p.currency) : "—"}
                    </td>
                    <td className="text-end">
                      {p.totalEgFee != null ? formatCurrency(p.totalEgFee, p.currency) : "—"}
                    </td>
                    <td className="text-end">
                      {p.totalStripeFee != null ? formatCurrency(p.totalStripeFee, p.currency) : "—"}
                    </td>
                    <td className="text-end">{p.numberOfOrders ?? "—"}</td>
                    <td>{formatDate(p.eventEndDate)}</td>
                    <td>
                      <Badge bg={statusVariant(p.status)}>{p.status}</Badge>
                    </td>
                    <td>{formatDate(p.createdAt)}</td>
                    <td>{p.createdBy?.email ?? p.createdBy?.uid ?? "—"}</td>
                    <td>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => openStatusModal(p)}
                        disabled={updatingId === p.id}
                      >
                        {updatingId === p.id ? <Spinner animation="border" size="sm" /> : "Change status"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {!loading && payouts.length > 0 && monthlySummary.length > 0 && (
        <Card className="mt-4">
          <Card.Header>
            <h6 className="mb-0">Monthly Summary</h6>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Month</th>
                  <th className="text-end">Net Payout</th>
                  <th className="text-end">Gross Amount</th>
                  <th className="text-end">EG Fee</th>
                  <th className="text-end">Stripe Fee</th>
                </tr>
              </thead>
              <tbody>
                {monthlySummary.map((row) => {
                  const labelDate = new Date(row.year, row.month, 1);
                  const monthLabel = labelDate.toLocaleString("default", {
                    month: "short",
                    year: "numeric",
                  });
                  return (
                    <tr key={`${row.year}-${row.month}`}>
                      <td>{monthLabel}</td>
                      <td className="text-end">
                        {formatCurrency(row.netPayout)}
                      </td>
                      <td className="text-end">
                        {row.grossAmount ? formatCurrency(row.grossAmount) : "—"}
                      </td>
                      <td className="text-end">
                        {row.egFee ? formatCurrency(row.egFee) : "—"}
                      </td>
                      <td className="text-end">
                        {row.stripeFee ? formatCurrency(row.stripeFee) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Modal show={showStatusModal} onHide={closeStatusModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change payout status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {payoutToEdit && (
            <>
              <p className="text-muted small mb-2">
                Event: <strong>{payoutToEdit.eventTitle || payoutToEdit.eventId || "—"}</strong>
                {" · "}
                {formatCurrency(payoutToEdit.amount, payoutToEdit.currency)}
              </p>
              <p className="mb-3">Current status: <Badge bg={statusVariant(payoutToEdit.status)}>{payoutToEdit.status}</Badge></p>
              <Form.Group>
                <Form.Label>New status</Form.Label>
                <Form.Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeStatusModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirmUpdateStatus}
            disabled={!newStatus || newStatus === payoutToEdit?.status}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
